import { Box, ButtonBase, Divider, Typography, makeStyles, Theme } from '@material-ui/core';
import { observer } from 'mobx-react';
import { EntityFieldProps } from './entityFieldProps';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;
const PERCENTAGE_STEP = 1;

const useStyles = makeStyles((theme: Theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 0),
  },
  name: {
    fontWeight: 700,
    fontSize: '1rem',
    marginRight: theme.spacing(2),
  },
  stepper: {
    display: 'flex',
    alignItems: 'stretch',
    flex: '0 0 auto',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  stepperButton: {
    width: 40,
    height: 40,
    fontSize: '1.25rem',
    lineHeight: 1,
    color: theme.palette.text.primary,
    backgroundColor: '#F5F5F5',
    '&:hover': {
      backgroundColor: '#EEEEEE',
    },
    '&:disabled': {
      color: theme.palette.text.disabled,
    },
  },
  valueCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 76,
    backgroundColor: theme.palette.common.white,
    borderLeft: '1px solid #E0E0E0',
    borderRight: '1px solid #E0E0E0',
  },
  valueInput: {
    width: 36,
    padding: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    textAlign: 'right',
    fontFamily: 'inherit',
    fontSize: '1rem',
    fontWeight: 700,
    color: 'inherit',
  },
  valueSuffix: {
    fontSize: '1rem',
    fontWeight: 700,
  },
}));

const toNumber = (value: any): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const clamp = (value: number) =>
  Math.min(MAX_PERCENTAGE, Math.max(MIN_PERCENTAGE, value));

const sanitize = (raw: string) => {
  const [whole, ...decimals] = raw.replace(/[^\d.]/g, '').split('.');
  const typed = decimals.length ? `${whole}.${decimals.join('')}` : whole;
  if (typed === '') return '';
  return Number(typed) > MAX_PERCENTAGE ? String(MAX_PERCENTAGE) : typed;
};

export const EntityBorrowersField = observer(({ store, item }: EntityFieldProps) => {
  const classes = useStyles();
  const rows = store.getExpected(item.itemName) ?? [];

  const setPercentage = (index: number, value: any) =>
    store.onBorrowerFieldChange(item.itemName, index, 'ownershipPercentage', value);

  const stepPercentage = (index: number, current: number | null, delta: number) =>
    setPercentage(index, Math.round(clamp((current ?? 0) + delta) * 100) / 100);

  return (
    <Box>
      {rows.map((row, index) => {
        const borrowerName = row?.borrowerName ?? '';
        const current = toNumber(row?.ownershipPercentage);
        return (
          <Box key={`${item.itemName}-${index}`}>
            <Box className={classes.row}>
              <Typography className={classes.name}>{borrowerName}</Typography>
              <Box className={classes.stepper}>
                <ButtonBase
                  className={classes.stepperButton}
                  onClick={() => stepPercentage(index, current, -PERCENTAGE_STEP)}
                  disabled={(current ?? 0) <= MIN_PERCENTAGE}
                  aria-label={`Decrease ownership for ${borrowerName}`}
                >
                  −
                </ButtonBase>
                <Box className={classes.valueCell}>
                  <input
                    className={classes.valueInput}
                    value={row?.ownershipPercentage ?? ''}
                    onChange={event => setPercentage(index, sanitize(event.target.value))}
                    inputMode="decimal"
                    aria-label={`Ownership percentage for ${borrowerName}`}
                    data-testid={`ownershipPercentage-${index}`}
                  />
                  <span className={classes.valueSuffix}>%</span>
                </Box>
                <ButtonBase
                  className={classes.stepperButton}
                  onClick={() => stepPercentage(index, current, PERCENTAGE_STEP)}
                  disabled={(current ?? 0) >= MAX_PERCENTAGE}
                  aria-label={`Increase ownership for ${borrowerName}`}
                >
                  +
                </ButtonBase>
              </Box>
            </Box>
            <Divider />
          </Box>
        );
      })}
    </Box>
  );
});
